import type { BookAction, ScenarioAnalysis } from '@prm/shared';
import { num, pct } from '../format';
import { PanelHead } from './Panel';

const ACTION_LABEL: Record<BookAction, string> = {
  trim: 'TRIM',
  hold: 'HOLD',
  protect: 'PROTECT',
  'trim-if-breach': 'TRIM IF BREACH',
};

const ACTION_TONE: Record<BookAction, string> = {
  trim: 'breach',
  hold: 'ok',
  protect: 'warn',
  'trim-if-breach': 'warn',
};

/** Per-position view with the recommended action and why it carries that tag. */
export function BookTable({ scenario }: { scenario: ScenarioAnalysis }) {
  return (
    <section className="panel">
      <PanelHead
        title="Book detail"
        description="Action tags and risk share, not raw position noise."
        sub={<span className="panel__sub">{scenario.book.length} positions</span>}
      />

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Ticker</th>
              <th className="cell-left">Role</th>
              <th>Conv</th>
              <th>Weight</th>
              <th>Beta</th>
              <th title="Share of portfolio variance, from the Euler decomposition">Risk share</th>
              <th className="cell-left" style={{ width: 132 }}>
                Action
              </th>
              <th className="cell-left">Why</th>
            </tr>
          </thead>
          <tbody>
            {scenario.book.map((row) => (
              <tr key={row.ticker}>
                <td className="ticker">{row.ticker}</td>
                <td className="cell-left">
                  <span className={`badge badge--role badge--${row.role}`}>{row.role}</span>
                </td>
                <td className="muted">{row.conviction}</td>
                <td>{pct(row.weight)}</td>
                <td className="muted">
                  {row.beta === null ? '—' : num(row.beta, 2)}
                  {row.betaStructural ? <sup className="sup sup--str">str</sup> : null}
                </td>
                <td className={(row.riskShare ?? 0) > 0.3 ? 'text-warn' : ''}>{pct(row.riskShare)}</td>
                <td className="cell-left">
                  <span className={`minichip minichip--${ACTION_TONE[row.action]}`}>{ACTION_LABEL[row.action]}</span>
                </td>
                <td className="cell-left cell-wrap muted">{row.actionReason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="footnote">
        Action tags come from the mitigation plan where it has an opinion and from policy where it does not, so this
        table never disagrees with the mitigations panel. Risk share is marginal contribution, not weight.
      </div>
    </section>
  );
}
